import { useEffect, useRef, useState, useCallback } from 'react';
import { useTheme } from '../contexts/ThemeContext';

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: string | HTMLElement,
        options: TurnstileRenderOptions
      ) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
      getResponse: (widgetId: string) => string | undefined;
      isExpired: (widgetId: string) => boolean;
    };
    onTurnstileLoad?: () => void;
  }
}

interface TurnstileRenderOptions {
  sitekey: string;
  callback?: (token: string) => void;
  'expired-callback'?: () => void;
  'error-callback'?: (errorCode: string) => void;
  'timeout-callback'?: () => void;
  theme?: 'light' | 'dark' | 'auto';
  size?: 'normal' | 'compact' | 'flexible';
  action?: string;
  retry?: 'auto' | 'never';
  'retry-interval'?: number;
  'refresh-expired'?: 'auto' | 'manual' | 'never';
  appearance?: 'always' | 'execute' | 'interaction-only';
  language?: string;
}

const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY || '';
const SCRIPT_ID = 'cf-turnstile-script';
const SCRIPT_LOAD_TIMEOUT_MS = 8000;

let scriptLoadPromise: Promise<boolean> | null = null;

function loadTurnstileScript(): Promise<boolean> {
  if (scriptLoadPromise) return scriptLoadPromise;

  scriptLoadPromise = new Promise<boolean>((resolve) => {
    if (window.turnstile) {
      resolve(true);
      return;
    }

    const existing = document.getElementById(SCRIPT_ID);
    if (existing) {
      const timeout = setTimeout(() => resolve(false), SCRIPT_LOAD_TIMEOUT_MS);
      window.onTurnstileLoad = () => {
        clearTimeout(timeout);
        resolve(true);
      };
      return;
    }

    const timeout = setTimeout(() => {
      resolve(false);
    }, SCRIPT_LOAD_TIMEOUT_MS);

    window.onTurnstileLoad = () => {
      clearTimeout(timeout);
      resolve(true);
    };

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src =
      'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onTurnstileLoad&render=explicit';
    script.async = true;
    script.defer = true;
    script.onerror = () => {
      clearTimeout(timeout);
      resolve(false);
    };
    document.head.appendChild(script);
  });

  return scriptLoadPromise;
}

interface UseTurnstileOptions {
  action?: string;
}

interface UseTurnstileReturn {
  turnstileRef: React.RefObject<HTMLDivElement>;
  token: string;
  isReady: boolean;
  isAvailable: boolean;
  resetTurnstile: () => void;
}

export function useTurnstile(options: UseTurnstileOptions = {}): UseTurnstileReturn {
  const { theme } = useTheme();
  const turnstileRef = useRef<HTMLDivElement>(null!);
  const widgetIdRef = useRef<string | null>(null);
  const [token, setToken] = useState('');
  const [isReady, setIsReady] = useState(false);
  const [isAvailable, setIsAvailable] = useState(false);

  const resetTurnstile = useCallback(() => {
    setToken('');
    if (widgetIdRef.current && window.turnstile) {
      try {
        window.turnstile.reset(widgetIdRef.current);
      } catch {
        // Widget may have been removed already
      }
    }
  }, []);

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) {
      setIsReady(true);
      setIsAvailable(false);
      return;
    }

    let cancelled = false;

    const init = async () => {
      const loaded = await loadTurnstileScript();

      if (cancelled) return;

      if (!loaded || !window.turnstile) {
        setIsReady(true);
        setIsAvailable(false);
        return;
      }

      setIsAvailable(true);

      if (!turnstileRef.current) {
        setIsReady(true);
        return;
      }

      try {
        if (widgetIdRef.current) {
          try {
            window.turnstile.remove(widgetIdRef.current);
          } catch {
            // ignore
          }
          widgetIdRef.current = null;
        }

        // A fresh widget starts unsolved, so drop any token from the previous one
        setToken('');

        const id = window.turnstile.render(turnstileRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          callback: (t: string) => {
            if (!cancelled) setToken(t);
          },
          'expired-callback': () => {
            if (!cancelled) setToken('');
          },
          'error-callback': () => {
            if (!cancelled) {
              setToken('');
              setIsAvailable(false);
            }
          },
          'timeout-callback': () => {
            if (!cancelled) setToken('');
          },
          theme,
          size: 'flexible',
          action: options.action,
          retry: 'never',
          'refresh-expired': 'auto',
          appearance: 'always',
        });

        widgetIdRef.current = id;
      } catch {
        if (!cancelled) setIsAvailable(false);
      }

      if (!cancelled) setIsReady(true);
    };

    init();

    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          // ignore
        }
        widgetIdRef.current = null;
      }
    };
  }, [options.action, theme]);

  return { turnstileRef, token, isReady, isAvailable, resetTurnstile };
}
